import { IExperience } from './experience.interface';
import { createTooltips, initReadMore, refreshAOS } from './global';
import { unescapeHTMLEntities, formatDate, getYear } from './utils';
import { displayError } from './education'

/**
 * Receives an array with experience data and fills the experience section.
 * 
 * @param experiences An Array with experiences to fill the experience section with.
 */
export function fillExperienceSection(experiences: IExperience[]) {
	const container = document.querySelector<HTMLDivElement>("#experienceContainer");
	if (!container) return displayError("Experience container not found.");

	// Create a shallow copy and reverse to prevent mutating the original array
	const reversedExperiences = [...experiences].reverse();

	// Generate HTML for all experiences
	const containerHtml = reversedExperiences.map(exp => generateExperienceHTML(exp)).join('');
	container.innerHTML = containerHtml;

	// Initialize tooltips and read more after the DOM update
	requestAnimationFrame(() => {
		createTooltips('.resumeTooltip');
		initReadMore();
	});
}

/**
 * Generates the HTML for an experience.
 * 
 * @param exp An experience object.
 * @returns The HTML string for the experience.
 */
function generateExperienceHTML(exp: IExperience): string {
	const startDateFull = formatDate(exp.start_date);
	const endDateFull = formatDate(exp.end_date);
	const startYear = getYear(exp.start_date);
	const endYear = getYear(exp.end_date);

	let expFaIcon = exp.career_break ? "fa-solid fa-calendar" : "fa-solid fa-briefcase";

	if (exp.title.toLowerCase().includes("hobby")) {
		expFaIcon = "fa-solid fa-laptop-code";
	}

	return `
		<div class="resume-wrapper" data-aos="zoom-in">
			<ul class="resume">
				<li>
					<div class="resume-tag">
						<i class="${expFaIcon}" aria-hidden="true"></i>
						<div class="resume-date">
							<span data-bs-toggle="tooltip" data-bs-placement="right"
								data-bs-container="#resume" data-bs-trigger="hover" class="resumeTooltip"
								title="${startDateFull}" aria-label="Start date: ${startDateFull}">
								${startYear}
							</span>
							<div class="separator"></div>
							<span data-bs-toggle="tooltip" data-bs-placement="right"
								data-bs-container="#resume" data-bs-trigger="hover" class="resumeTooltip"
								title="${endDateFull}" aria-label="End date: ${endDateFull}">
								${endYear}
							</span>
						</div>
					</div>
					<div class="timeline-item">
						<span class="timeline-location ${exp.title.length > 50 ? "long-title" : ""}">
							<i class="fa-solid fa-location-dot" aria-hidden="true"></i> ${exp.location.toUpperCase()}
						</span>
						<h3 class="timeline-header">${unescapeHTMLEntities(exp.title).toUpperCase()}</h3>
						<div class="timeline-body">
							${exp.career_break ? `
								<h4>
									<i class="fa-regular fa-calendar" aria-hidden="true"></i>
									&nbsp;&nbsp;CAREER BREAK
								</h4>
							` : exp.company ? `
								<h4>
									<i class="fa-solid fa-building resumeTooltip" aria-hidden="true"
									data-bs-toggle="tooltip" data-bs-container="#resume" data-bs-trigger="hover"
									title="Company"></i>&nbsp;&nbsp;
									${exp.company_url ? `
										<a href="${exp.company_url}" target="_blank" rel="noopener noreferrer">
											${exp.company.toUpperCase()}
										</a>
									` : exp.company.toUpperCase()}
									<span style="text-transform: none;">
										${exp.level ? `
											&nbsp;&nbsp;&middot;&nbsp;&nbsp;
											<span data-bs-toggle="tooltip" data-bs-container="#resume" data-bs-trigger="hover"
												data-bs-custom-class="skill-rating-tooltip"
												class="resumeTooltip" title="${exp.level.description}" aria-label="Level: ${exp.level.title}">
												<strong>${exp.level.title}</strong>
											</span>
										` : ''}
										&nbsp;&nbsp;&middot;&nbsp;&nbsp;${exp.employment_type || ''}
										&nbsp;&nbsp;&middot;&nbsp;&nbsp;${exp.location_model || ''}
									</span>
								</h4>
							` : ''}
							<div class="exp-description">${exp.description}</div>
						</div>
					</div>
				</li>
			</ul>
		</div>`;
}

/**
 * Retrieve all the experience data from the JSON file.
 * 
 * @returns {Promise<IExperience[]>} An array with experiences.
 */
async function loadExperienceJson(): Promise<IExperience[]> {
	const url = 'json/experience.json?v=4';
	let response = await fetch(url);

	if (response.ok) {
		return await response.json();
	} else {
		throw new Error("Error fetching experience data. Response status: " + response.status);
	}
}

export function experienceSetup() {
	/// Load experience
	loadExperienceJson()
	.then((experience) => {
		fillExperienceSection(experience);
		refreshAOS(); // Refresh AOS to recognize new elements
	})
	.catch((e) => {
		displayError(e.message);
	});
}